import { type JobTicketStore } from '@/features/jobs/tickets/JobTicketStore'
import { type Job } from '@/features/jobs/Job'
import type { AlertRetryWrapperFactory } from '@/features/shell'
import { computed } from 'mobx'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'

/**
 * View model for the job's tickets.
 */
export class JobTicketsViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  constructor(
    private readonly _job: Job,
    private readonly _jobTicketStore: JobTicketStore,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {}

  /**
   * Gets the latest ticket for the job, if any.
   */
  @computed
  get latestTicket() {
    return this._jobTicketStore.getLatestTicketForJob(this._job.id)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    void this._fetch().catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  private async _fetch() {
    const { signal } = this._lifecycle
    await this._createAlertRetryWrapper(
      'Loading the tickets failed',
      signal,
    )(() =>
      this._jobTicketStore.fetchLatestOpenTicketsForJobs(
        [this._job.id],
        signal,
      ),
    )
  }
}
